import React, { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  Checkbox,
  TextField,
  Stack,
  Divider,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import AppButton from "./AppButton";
import AddIcon from "@mui/icons-material/Add";
import theme from "../utils/theme";
import IconButtons from "./IconButtons";

// Styles object
const styles = {
  root: {
    mt: 3,
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    mt: 2,
    mb: 1,
    width: "100%",
  },
  title: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: 18,
    letterSpacing: 0.1,
    textAlign: "left",
    mr: 2,
  },
  divider: {
    borderColor: theme.palette.grey[300],
    opacity: 1,
  },
  partsCount: {
    color: theme.palette.text.disabled,
    ml: 2,
    whiteSpace: "nowrap",
  },
  list: {
    minHeight: "150px",
    maxHeight: "250px",
    overflowY: "auto",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: "4px",
    mt: 2,
    mb: 1,
    p: 1,
  },
  emptyListItem: {
    justifyContent: "center",
    py: 6,
  },
  emptyText: {
    color: theme.palette.text.disabled,
  },
  listItem: (part) => ({
    py: 1,
    mb: 1,
    borderRadius: 2,
    bgcolor: part.sticker_pasted
      ? theme.palette.background.default
      : "transparent",
    "&:hover": {
      bgcolor: part.sticker_pasted
        ? theme.palette.success.light + "1F"
        : theme.palette.action.hover,
    },
    display: "flex",
    alignItems: "center",
  }),
  indexNumber: {
    minWidth: "24px",
    color: theme.palette.text.disabled,
    fontWeight: 500,
  },
  checkboxContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mr: 2,
    ml: 2,
  },
  checkboxLabel: {
    fontWeight: 500,
    mr: 1,
  },
  deleteButton: {
    ml: "auto",
  },
  errorText: {
    mt: 0.5,
    fontSize: "0.75rem",
  },
};

/**
 * VisualCheckForm - Reusable component for managing visual check parts with sticker validation
 */
export default function VisualCheckForm({
  values,
  errors,
  touched,
  setFieldValue,
  viewOnly = false,
  handleDeletePart,
}) {
  const parts = values.visual_parts || [];
  const listRef = useRef(null);
  const newPartRef = useRef(null);
  const previousPartsCountRef = useRef(parts.length);

  // Handle part name change using Formik
  const handlePartNameChange = (idx, value) => {
    const updatedParts = [...parts];
    updatedParts[idx] = { ...updatedParts[idx], part_name: value };
    setFieldValue("visual_parts", updatedParts);
  };

  // Handle checkbox change using Formik
  const handlePartCheckedChange = (idx, checked) => {
    const updatedParts = [...parts];
    updatedParts[idx] = { ...updatedParts[idx], sticker_pasted: checked };
    setFieldValue("visual_parts", updatedParts);
  };

  // Add new part using Formik
  const handleAddPart = () => {
    const updatedParts = [...parts, { part_name: "", sticker_pasted: false }];
    setFieldValue("visual_parts", updatedParts);
  };

  // Scroll to newly added part
  useEffect(() => {
    if (parts.length > previousPartsCountRef.current) {
      // A new part was added
      setTimeout(() => {
        if (newPartRef.current) {
          newPartRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }, 100); // Small timeout to ensure the DOM is updated
    }
    previousPartsCountRef.current = parts.length;
  }, [parts.length]);

  return (
    <Box sx={styles.root}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box sx={styles.headerContainer}>
          <Typography variant="subtitle1" sx={styles.title}>
            Visual Check
          </Typography>
          <Box sx={{ flex: 1, ml: 1, alignSelf: "center" }}>
            <Divider sx={styles.divider} />
          </Box>
        </Box>
        {parts.length > 0 ? (
          <Typography variant="body2" sx={styles.partsCount}>
            {parts.length} {parts.length === 1 ? "part" : "parts"} added
          </Typography>
        ) : null}
      </Stack>

      <List dense sx={styles.list} ref={listRef}>
        {parts.length === 0 ? (
          <ListItem sx={styles.emptyListItem}>
            <Typography variant="body1" sx={styles.emptyText}>
              No visual parts added
            </Typography>
          </ListItem>
        ) : (
          parts.map((part, idx) => {
            const hasPartError =
              errors.visual_parts?.[idx]?.part_name &&
              touched.visual_parts?.[idx]?.part_name;
            const hasStickerError =
              errors.visual_parts?.[idx]?.sticker_pasted &&
              touched.visual_parts?.[idx]?.sticker_pasted;

            // Set ref for the last item (newly added part)
            const isNewPart = idx === parts.length - 1;

            return (
              <ListItem
                key={idx}
                sx={styles.listItem(part)}
                ref={isNewPart ? newPartRef : null}
              >
                <Typography variant="body2" sx={styles.indexNumber}>
                  {idx + 1}.
                </Typography>

                <Box sx={{ mr: 4 }}>
                  <TextField
                    label={`Part Name #${idx + 1}`}
                    value={part.part_name}
                    onChange={(e) => handlePartNameChange(idx, e.target.value)}
                    size="small"
                    sx={{ width: 320 }}
                    variant="outlined"
                    color={part.sticker_pasted ? "success" : "primary"}
                    disabled={viewOnly}
                    error={hasPartError}
                    helperText={
                      hasPartError ? errors.visual_parts?.[idx]?.part_name : ""
                    }
                  />
                </Box>

                <Box sx={styles.checkboxContainer}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" sx={styles.checkboxLabel}>
                      Sticker Pasted :
                    </Typography>
                    <Checkbox
                      checked={part.sticker_pasted}
                      onChange={(e) =>
                        handlePartCheckedChange(idx, e.target.checked)
                      }
                      color="primary"
                      disabled={viewOnly}
                    />
                  </Box>
                  {hasStickerError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={styles.errorText}
                    >
                      {errors.visual_parts?.[idx]?.sticker_pasted}
                    </Typography>
                  )}
                </Box>

                {!viewOnly && (
                  <Tooltip title="Remove" arrow placement="top">
                    <IconButtons
                      onClick={() =>
                        handleDeletePart(
                          { ...part, idx: idx },
                          values,
                          setFieldValue
                        )
                      }
                      buttonType="delete"
                      size="small"
                      sx={styles.deleteButton}
                    />
                  </Tooltip>
                )}
              </ListItem>
            );
          })
        )}
      </List>

      {!viewOnly && (
        <AppButton
          variant="contained"
          buttonType="main"
          startIcon={<AddIcon />}
          onClick={handleAddPart}
          sx={{ mt: 1 }}
          label="Add Part"
        />
      )}

      {errors.parts && touched.parts && typeof errors.parts === "string" && (
        <FormHelperText error sx={{ mt: 1 }}>
          {errors.parts}
        </FormHelperText>
      )}
    </Box>
  );
}
